/**
 * Plausible custom events.
 *
 * The script in index.html already captures pageviews (including the SPA route
 * changes, it patches history.pushState), outbound links, file downloads and a
 * generic `Form: Submission`. Everything here is the layer on top: the clicks
 * and outcomes that say *why* a visit was worth something, with properties
 * attached so one event name stays readable in the dashboard instead of
 * splitting into a dozen near-duplicates.
 *
 * Naming follows what the script itself emits — `Noun: Verb`.
 */

type PlausibleProps = Record<string, string | number | boolean>;

type PlausibleFn = (
  event: string,
  options?: { props?: PlausibleProps; callback?: () => void },
) => void;

declare global {
  interface Window {
    plausible?: PlausibleFn & { q?: unknown[] };
  }
}

/** Where on the site an interaction happened. Keeps one event name per intent. */
export type TrackLocation =
  | 'nav'
  | 'hero'
  | 'services'
  | 'contact-section'
  | 'contact-page'
  | 'footer';

export const ANALYTICS_EVENTS = {
  ctaClick: 'CTA: Click',
  serviceExpand: 'Service: Expand',
  serviceCollapse: 'Service: Collapse',
  contactStart: 'Contact: Start',
  contactSubmit: 'Contact: Submit',
  contactSuccess: 'Contact: Success',
  contactError: 'Contact: Error',
  navClick: 'Nav: Click',
  footerClick: 'Footer: Click',
} as const;

type AnalyticsEvent = (typeof ANALYTICS_EVENTS)[keyof typeof ANALYTICS_EVENTS];

/**
 * The script installs a queueing stub before it finishes loading, so a call
 * that lands early is replayed rather than lost. It can still be absent
 * entirely — an ad blocker, or a dev server without the tag — which is why
 * nothing here may throw: analytics must never be what breaks a form.
 */
export function track(event: AnalyticsEvent, props?: PlausibleProps): void {
  try {
    window.plausible?.(event, props ? { props } : undefined);
  } catch {
    // Reporting a failure to report is not worth a broken interaction.
  }
}

export const trackCta = (label: string, location: TrackLocation) =>
  track(ANALYTICS_EVENTS.ctaClick, { label, location });

export const trackService = (service: string, expanded: boolean) =>
  track(
    expanded ? ANALYTICS_EVENTS.serviceExpand : ANALYTICS_EVENTS.serviceCollapse,
    { service },
  );

export const trackNav = (target: string) =>
  track(ANALYTICS_EVENTS.navClick, { target });

export const trackFooter = (target: string) =>
  track(ANALYTICS_EVENTS.footerClick, { target });
